// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Cursors } from '@shared/commons/Cursors';
import { Point, Rectangle, Size } from '@shared/commons/Geometry';
import { Pen, SolidBrush } from '@shared/commons/Graphics';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { TerceraChartBaseRenderer } from './TerceraChartBaseRenderer';
export class DateRangeBorder {
    constructor() {
        this.isVisible = true;
        this.isCollapsed = false;
        this.Label = '';
        this.AdditionalLabel = '';
        this.Value = NaN;
    }
    GetText() {
        return this.Label + (isValidString(this.AdditionalLabel) ? this.AdditionalLabel : '');
    }
}
export class TerceraChartRangeRenderer extends TerceraChartBaseRenderer {
    constructor(chart) {
        super(chart);
        this._font = ThemeManager.Fonts.Font_12F_regular;
        this._isHovered = false;
        this._isMoving = false;
        this._isCollapseButtonHovered = false;
        this._hoveredBorder = null;
        this._movingBorder = null;
        this.foregroundColor = '';
        this.backgroundColor = '';
        this.leftBorder = new DateRangeBorder();
        this.rightBorder = new DateRangeBorder();
        this.UseInAutoscale = false;
    }
    Draw(gr, window, windowsContainer, advParams) {
        if (!this.Visible) {
            return;
        }
        const windowClientRectangle = window.ClientRectangle;
        if (this.leftBorder.isVisible && !isNaN(this.leftBorder.Value)) {
            const screenX = window.PointsConverter.GetScreenX(this.leftBorder.Value);
            const isVisible = windowClientRectangle.Contains(screenX, windowClientRectangle.Y);
            if (isVisible) {
                this.DrawLine(gr, windowClientRectangle, screenX, this.leftBorder.isCollapsed, this.leftBorder.GetText(), true);
                this.DrawPlate(gr, window, windowsContainer, this.leftBorder.Value);
            }
        }
        if (this.rightBorder.isVisible && !isNaN(this.rightBorder.Value)) {
            const screenX = window.PointsConverter.GetScreenX(this.rightBorder.Value);
            const isVisible = windowClientRectangle.Contains(screenX, windowClientRectangle.Y);
            if (isVisible) {
                this.DrawLine(gr, windowClientRectangle, screenX, this.rightBorder.isCollapsed, this.rightBorder.GetText(), false);
                this.DrawPlate(gr, window, windowsContainer, this.rightBorder.Value);
            }
        }
    }
    DrawLine(gr, windowClientRectangle, screenX, isCollapsed, text, isLeftBorder) {
        const textWidth = 88;
        const collapseButtonRectangle = new Rectangle(screenX - this._font.Height / 2 - 1, windowClientRectangle.Top() + 35 + (isCollapsed ? 0 : textWidth), this._font.Height + 2, 15);
        const plateRectangle = new Rectangle(collapseButtonRectangle.X, windowClientRectangle.Top(), collapseButtonRectangle.Width, collapseButtonRectangle.Bottom() - windowClientRectangle.Top());
        const backgroundColor = this.backgroundColor;
        const transparentBackgroundColor = `${backgroundColor}C8`;
        const haloPenColor = `${backgroundColor}${this._isHovered ? '96' : '32'}`;
        const backgroundBrush = new SolidBrush(backgroundColor);
        const transparentBackgroundBrush = new SolidBrush(transparentBackgroundColor);
        const linePen = new Pen(backgroundColor, 1);
        const haloPen = new Pen(haloPenColor, 7);
        gr.DrawLine(haloPen, screenX, plateRectangle.Bottom(), screenX, windowClientRectangle.Bottom());
        gr.DrawLine(linePen, screenX, plateRectangle.Bottom(), screenX, windowClientRectangle.Bottom());
        gr.RoundRectangle(plateRectangle, 2, transparentBackgroundBrush, linePen);
        gr.RoundRectangle(collapseButtonRectangle, 2, backgroundBrush, linePen);
        const foregroundBrush = new SolidBrush(this.foregroundColor);
        const foregroundPen = new Pen(this.foregroundColor, 1);
        const arrowSize = new Size(3, 5);
        const arrowRect = new Rectangle(plateRectangle.X + (plateRectangle.Width - arrowSize.Width) / 2, plateRectangle.Y + 15, arrowSize.Width, arrowSize.Height);
        const dotsRect = new Rectangle(collapseButtonRectangle.X + 1, collapseButtonRectangle.Y + 2, collapseButtonRectangle.Width, collapseButtonRectangle.Height / 2);
        const arrowPoints = [];
        if (isLeftBorder) {
            arrowPoints.push(new Point(arrowRect.X, arrowRect.Y));
            arrowPoints.push(new Point(arrowRect.X + arrowRect.Width, arrowRect.Y + arrowRect.Height / 2));
            arrowPoints.push(new Point(arrowRect.X, arrowRect.Y + arrowRect.Height));
        }
        else {
            arrowPoints.push(new Point(arrowRect.X + arrowRect.Width, arrowRect.Y));
            arrowPoints.push(new Point(arrowRect.X, arrowRect.Y + arrowRect.Height / 2));
            arrowPoints.push(new Point(arrowRect.X + arrowRect.Width, arrowRect.Y + arrowRect.Height));
        }
        gr.DrawLines(foregroundPen, arrowPoints);
        gr.DrawStringInRect('..', this._font, foregroundBrush, dotsRect, false);
        if (!isCollapsed && isValidString(text)) {
            gr.save();
            gr.translate(plateRectangle.X + plateRectangle.Width / 2, plateRectangle.Y + plateRectangle.Height / 2);
            gr.rotate((Math.PI / 2) * -1);
            gr.translate(-(plateRectangle.X + plateRectangle.Width / 2), -(plateRectangle.Y + plateRectangle.Height / 2));
            gr.DrawString(text, this._font, foregroundBrush, plateRectangle.X - plateRectangle.Height / 2 + collapseButtonRectangle.Height + 15, plateRectangle.Y + Math.floor(plateRectangle.Height / 2) + 2, 'left', 'middle');
            gr.restore();
        }
    }
    DrawPlate(gr, window, windowsContainer, dataX) {
        if (isNullOrUndefined(windowsContainer.xScaleRenderer) || !windowsContainer.xScaleRenderer.Visible) {
            return;
        }
        const screenX = window.PointsConverter.GetScreenX(dataX);
        const formattedDataX = windowsContainer.xScaleRenderer.FormatValue(dataX);
        const textWidth = gr.GetTextWidth(formattedDataX, windowsContainer.xScaleRenderer.settings.ScaleFont);
        const xScaleRect = windowsContainer.xScaleRenderer.Rectangle;
        const xScaleFont = windowsContainer.xScaleRenderer.settings.ScaleFont;
        const xPlateRect = new Rectangle(screenX - textWidth / 2 - 3, xScaleRect.Y, textWidth + 6, xScaleRect.Height);
        if (xPlateRect.Left() >= xScaleRect.Left() && xPlateRect.Right() <= xScaleRect.Right()) {
            const backgroundBrush = new SolidBrush(`${this.backgroundColor}C8`);
            const foregroundBrush = new SolidBrush(this.foregroundColor);
            gr.FillRect(backgroundBrush, xPlateRect.X, xPlateRect.Y, xPlateRect.Width, xPlateRect.Height);
            gr.DrawStringInRect(formattedDataX, xScaleFont, foregroundBrush, xPlateRect, false);
        }
    }
    ThemeChanged() {
        super.ThemeChanged();
        this.foregroundColor = ThemeManager.colors.Chart_RangeRendererForeColor;
        this.backgroundColor = ThemeManager.colors.Chart_RangeRendererBackColor;
    }
    Localize() {
        super.Localize();
        this._font = ThemeManager.Fonts.Font_11F25_regular;
    }
    ProcessMouseDown(e) {
        if (!this.Visible) {
            return false;
        }
        let processed = super.ProcessMouseMove(e);
        if (isNullOrUndefined(e.window)) {
            return processed;
        }
        if (this._isCollapseButtonHovered) {
            this.leftBorder.isCollapsed = !this.leftBorder.isCollapsed;
            this.rightBorder.isCollapsed = !this.rightBorder.isCollapsed;
            this._isCollapseButtonHovered = false;
            processed = true;
        }
        else if (this._isHovered) {
            this._isMoving = true;
            this._movingBorder = this._hoveredBorder;
            processed = true;
        }
        if (processed) {
            e.NeedRedraw = true;
        }
        return processed;
    }
    ProcessMouseUp(e) {
        if (!this.Visible) {
            return false;
        }
        let processed = super.ProcessMouseMove(e);
        if (isNullOrUndefined(e.window)) {
            return processed;
        }
        if (this._isMoving) {
            this._isMoving = false;
            this._movingBorder = null;
            processed = true;
        }
        return processed;
    }
    ProcessMouseMove(e) {
        if (!this.Visible) {
            return false;
        }
        let processed = super.ProcessMouseMove(e);
        if (isNullOrUndefined(e.window)) {
            return processed;
        }
        let isHovered = false;
        let isCollapserHovered = false;
        let hoveredBorder = null;
        let isMoved = false;
        const hoverOffset = this._font.Height + 2 / 2;
        if (this.leftBorder.isVisible && !isNaN(this.leftBorder.Value)) {
            const screenX = e.window.PointsConverter.GetScreenX(this.leftBorder.Value);
            if (e.Location.X >= screenX - hoverOffset && e.Location.X <= screenX + hoverOffset) {
                isHovered = true;
                hoveredBorder = this.leftBorder;
                const collapseButtonRectangle = new Rectangle(screenX - this._font.Height / 2 - 1, e.window.ClientRectangle.Top() + 35 + (this.leftBorder.isCollapsed ? 0 : 88), this._font.Height + 2, 15);
                if (collapseButtonRectangle.Contains(e.Location.X, e.Location.Y)) {
                    isCollapserHovered = true;
                }
            }
            if (this._isMoving && this._movingBorder === this.leftBorder) {
                this.leftBorder.Value = e.window.PointsConverter.GetDataX(e.Location.X);
                isMoved = true;
            }
        }
        if (this.rightBorder.isVisible && !isNaN(this.rightBorder.Value)) {
            const screenX = e.window.PointsConverter.GetScreenX(this.rightBorder.Value);
            if (e.Location.X >= screenX - hoverOffset && e.Location.X <= screenX + hoverOffset) {
                isHovered = true;
                hoveredBorder = this.rightBorder;
                const collapseButtonRectangle = new Rectangle(screenX - this._font.Height / 2 - 1, e.window.ClientRectangle.Top() + 35 + (this.rightBorder.isCollapsed ? 0 : 88), this._font.Height + 2, 15);
                if (collapseButtonRectangle.Contains(e.Location.X, e.Location.Y)) {
                    isCollapserHovered = true;
                }
            }
            if (this._isMoving && this._movingBorder === this.rightBorder) {
                this.rightBorder.Value = e.window.PointsConverter.GetDataX(e.Location.X);
                isMoved = true;
            }
        }
        this._hoveredBorder = hoveredBorder;
        if (this._isHovered !== isHovered) {
            this._isHovered = isHovered;
            processed = true;
        }
        if (isMoved) {
            processed = true;
        }
        if (this._isCollapseButtonHovered !== isCollapserHovered) {
            this._isCollapseButtonHovered = isCollapserHovered;
            processed = true;
        }
        e.NeedRedraw = processed;
        return processed;
    }
    GetCursor(e) {
        let cursor = super.GetCursor(e);
        if (this._isCollapseButtonHovered) {
            cursor = Cursors.Hand;
        }
        else if (this._isHovered || this._isMoving) {
            cursor = Cursors.VSplit;
        }
        return cursor;
    }
}
